import type { InSnipCard } from "./types";

/**
 * "Your InSnip Recap", a Spotify-Wrapped-style snapshot of a reader's habit,
 * computed entirely from local state (no backend, no PII). Shown at the end of
 * the drop and from the menu. Pure + testable.
 */

export type Recap = {
  cardsRead: number;
  streak: number;
  saved: number;
  reactions: number;
  topCategory: string | null;
  topReaction: string | null;
  minutesSaved: number;
};

function mostCommon<T extends string>(items: T[]): T | null {
  if (items.length === 0) return null;
  const counts = new Map<T, number>();
  for (const item of items) counts.set(item, (counts.get(item) ?? 0) + 1);
  let best: T | null = null;
  let bestN = -1;
  for (const [item, n] of counts) {
    if (n > bestN) {
      best = item;
      bestN = n;
    }
  }
  return best;
}

export function computeRecap(input: {
  seenIds: Iterable<string>;
  cards: InSnipCard[];
  streak: number;
  reactions: Record<string, string[]>;
  savedCount: number;
  /** Avg minutes to read a full article vs an InSnip snippet (for "time saved"). */
  minutesPerStorySaved?: number;
}): Recap {
  const seen = new Set(input.seenIds);
  const byId = new Map(input.cards.map((c) => [c.id, c]));
  const seenCategories: string[] = [];
  for (const id of seen) {
    const card = byId.get(id);
    if (card?.category) seenCategories.push(card.category);
  }
  const allReactions = Object.values(input.reactions).flat();
  const perStory = input.minutesPerStorySaved ?? 3;

  return {
    cardsRead: seen.size,
    streak: Math.max(0, input.streak),
    saved: Math.max(0, input.savedCount),
    reactions: allReactions.length,
    topCategory: mostCommon(seenCategories),
    topReaction: mostCommon(allReactions),
    minutesSaved: Math.round(seen.size * perStory),
  };
}
